import * as z from 'zod';
import { isoDateSchema, isoDateTimeSchema, machineKeySchema, strict, uuidSchema, } from './common.js';
import { personStatusSchema } from './person.js';
/**
 * Exports: the administrator's CSV, and the single-subject data export.
 *
 * Both produce a file that holds decrypted personal data, which is the one place in this system
 * where that is allowed and is precisely why it is a deliberate, audited operation with an expiry
 * rather than a link somebody can leave lying around. The file is encrypted at rest, it expires,
 * and its filters are stored encrypted too: a filter is `country = MA`, which is itself a statement
 * about people.
 */
/**
 * `credential_batch` is the one kind whose retention is not `retention.exports_days` (AD-63).
 *
 * It outlives its batch. `credential_blanks` stores a one-way keyed hash and no code, so a
 * database restored from an archive taken BEFORE the import has no record of those bands at all,
 * and re-importing the artifact is the only way back.
 */
export const exportKindSchema = z.enum(['csv_admin', 'person_dsar', 'credential_batch']);
export const exportStatusSchema = z.enum(['queued', 'running', 'ready', 'failed', 'expired']);
/**
 * What an administrator's CSV covers.
 *
 * `columns` names field keys rather than accepting an expression, so an export cannot become an
 * ad-hoc query language over encrypted data.
 */
export const exportFilterSchema = strict({
    status: z.array(personStatusSchema).optional(),
    createdFrom: isoDateSchema.optional(),
    createdTo: isoDateSchema.optional(),
    lastActivityFrom: isoDateSchema.optional(),
    lastActivityTo: isoDateSchema.optional(),
    countries: z.array(z.string().length(2)).max(250).optional(),
    columns: z.array(machineKeySchema).min(1).max(100),
}).refine((filter) => filter.createdFrom === undefined ||
    filter.createdTo === undefined ||
    filter.createdFrom <= filter.createdTo, { error: 'errors.validation.range_inverted', path: ['createdTo'] });
export const createExportRequestSchema = z.discriminatedUnion('kind', [
    strict({ kind: z.literal('csv_admin'), filters: exportFilterSchema }),
    strict({ kind: z.literal('person_dsar'), personId: uuidSchema }),
]);
/**
 * One export row.
 *
 * **`expiresAt` is nullable, and the null is AD-63's exemption made visible.** A `csv_admin` and a
 * `person_dsar` artifact are both on `retention.exports_days`, so both carry a date. A
 * `credential_batch` artifact is not on any clock: `credential_blanks` holds a one-way keyed hash
 * and no code, so a database restored from an archive taken BEFORE the import has no record of
 * those bands and re-importing the file is the only way back. Declaring the column non-nullable
 * would have made that artifact unrepresentable in the very response an administrator uses to find
 * it, and `export-cleanup` already refuses to delete a row whose `expires_at` is null.
 */
export const exportSchema = strict({
    id: uuidSchema,
    kind: exportKindSchema,
    status: exportStatusSchema,
    subjectPersonId: uuidSchema.nullable(),
    rowCount: z.int().nonnegative().nullable(),
    bytes: z.int().nonnegative().nullable(),
    expiresAt: isoDateTimeSchema.nullable(),
    downloadedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    createdBy: uuidSchema.nullable(),
});
/**
 * What requesting an export answers with: the row, and the one-time password its file is sealed
 * under.
 *
 * The password is shown exactly once and stored nowhere. The artifact on disk is encrypted with it,
 * so an administrator who loses it re-requests the export rather than recovering the file — which
 * is the correct trade for a file holding decrypted personal data, and the same trade the batch
 * artifact of AD-63 makes.
 */
export const exportCreatedSchema = strict({
    export: exportSchema,
    password: z.string().min(16).max(128),
});
/** Listing exports. Filtered by kind, because the batch artifact belongs on a different screen. */
export const exportQuerySchema = strict({
    kind: exportKindSchema.optional(),
});
export const exportListSchema = strict({ items: z.array(exportSchema) });
//# sourceMappingURL=export.js.map